export const CASH_FLOW_TYPE = {
  INCOME: "income",
  OUTCOME: "outcome",
};

export const CASH_FLOW_STATUS = {
  APPROVED: "Đã xét duyệt",
  PENDING: "Chờ xét duyệt",
  REJECTED: "Đã từ chối",
};

export const RECURRING_CASH_FLOW_CATEGORIES = ["Mặt bằng", "Internet", "Điện", "Nước"];

export const RECURRING_MATRIX_ROWS = [
  { key: "location", label: "Mặt bằng", category: "Mặt bằng" },
  { key: "internet", label: "Internet", category: "Internet" },
  { key: "electricity", label: "Điện", category: "Điện" },
  { key: "water", label: "Nước", category: "Nước" },
  { key: "marketing", label: "Marketing", category: "Marketing" },
  { key: "salary", label: "Lương nhân viên", category: "Lương" },
  { key: "other", label: "Khác", category: "Chi khác" },
];

const createRecurringMatrixCell = () => ({
  grossTotal: 0,
  effectiveTotal: 0,
  pendingTotal: 0,
  recordCount: 0,
  staffCount: 0,
  statusCounts: { approved: 0, pending: 0, rejected: 0 },
  statusTotals: { approved: 0, pending: 0, rejected: 0 },
  recordIds: [],
  recordIdsByStatus: { approved: [], pending: [], rejected: [] },
});

const normalizeRecurringCategories = (sourceCategories = {}) =>
  RECURRING_MATRIX_ROWS.reduce((result, row) => {
    const sourceCell = sourceCategories?.[row.key] || {};
    const emptyCell = createRecurringMatrixCell();
    result[row.key] = {
      ...emptyCell,
      ...sourceCell,
      statusCounts: { ...emptyCell.statusCounts, ...sourceCell.statusCounts },
      statusTotals: { ...emptyCell.statusTotals, ...sourceCell.statusTotals },
      recordIds: Array.isArray(sourceCell.recordIds) ? sourceCell.recordIds : [],
      recordIdsByStatus: {
        ...emptyCell.recordIdsByStatus,
        ...sourceCell.recordIdsByStatus,
      },
    };
    return result;
  }, {});

export const normalizeRecurringYearData = (payload, fallbackYear) => {
  const sourceMonths = Array.isArray(payload?.months) ? payload.months : [];
  const months = Array.from({ length: 12 }, (_, monthIndex) => {
    const sourceMonth = sourceMonths.find(
      (item) => Number(item?.month) === monthIndex + 1,
    );
    const categories = normalizeRecurringCategories(sourceMonth?.categories);

    const totals = RECURRING_MATRIX_ROWS.reduce(
      (result, row) => {
        result.grossTotal += Number(categories[row.key].grossTotal) || 0;
        result.effectiveTotal += Number(categories[row.key].effectiveTotal) || 0;
        result.pendingTotal += Number(categories[row.key].pendingTotal) || 0;
        result.recordCount += Number(categories[row.key].recordCount) || 0;
        return result;
      },
      { grossTotal: 0, effectiveTotal: 0, pendingTotal: 0, recordCount: 0 },
    );

    return { month: monthIndex + 1, categories, totals };
  });

  return {
    year: Number(payload?.year) || Number(fallbackYear),
    months,
    allTimeCategories: normalizeRecurringCategories(payload?.allTimeCategories),
  };
};

export const getRecurringCellStatus = (cell, isSalary = false) => {
  if (isSalary) return cell?.grossTotal > 0 ? "automatic" : "empty";

  const activeStatuses = Object.keys(cell?.statusCounts || {}).filter(
    (key) => Number(cell.statusCounts[key]) > 0,
  );
  if (!activeStatuses.length) return "empty";
  return activeStatuses.length === 1 ? activeStatuses[0] : "mixed";
};

export const buildRecurringKpis = (summary) => {
  const categories = summary?.categories || {};
  const getCell = (key) => ({ ...createRecurringMatrixCell(), ...categories[key] });
  const kpis = ["location", "internet", "electricity", "water"].reduce(
    (result, key) => {
      const cell = getCell(key);
      result[key] = {
        value: Number(cell.effectiveTotal) || 0,
        recordCount: Number(cell.recordCount) || 0,
        staffCount: 0,
        pendingTotal: Number(cell.pendingTotal) || 0,
      };
      return result;
    },
    {},
  );
  const otherCells = [getCell("marketing"), getCell("salary"), getCell("other")];
  kpis.other = otherCells.reduce(
    (result, cell) => {
      result.value += Number(cell.effectiveTotal) || 0;
      result.recordCount += Number(cell.recordCount) || 0;
      result.staffCount += Number(cell.staffCount) || 0;
      result.pendingTotal += Number(cell.pendingTotal) || 0;
      return result;
    },
    { value: 0, recordCount: 0, staffCount: 0, pendingTotal: 0 },
  );

  return kpis;
};

const padNumber = (value, length = 2) => String(value).padStart(length, "0");

const parseCashFlowDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const normalized = String(value).trim();
  const vietnameseDate = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (vietnameseDate) {
    const [, day, month, year] = vietnameseDate;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const dateOnly = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const parseCashFlowAmount = (value) => {
  if (value === null || value === undefined || value === "") return 0;

  const normalized = String(value)
    .trim()
    .replace(/\./g, "")
    .replace(/,/g, "");
  const amount = Number(normalized);

  return Number.isFinite(amount) ? amount : 0;
};

export const normalizeCashInputValue = (value) =>
  String(value ?? "").replace(/[^0-9]/g, "");

export const isCashFlowIncludedInStats = (item) => item?.time === "now";

export const isCashFlowApproved = (item) =>
  item?.status === CASH_FLOW_STATUS.APPROVED;

export const normalizeCashFlowItem = (item, type) => {
  const amount = parseCashFlowAmount(item?.value);
  const normalizedType =
    type === CASH_FLOW_TYPE.OUTCOME ? CASH_FLOW_TYPE.OUTCOME : CASH_FLOW_TYPE.INCOME;

  return {
    ...item,
    type: normalizedType,
    cashFlowId: `${normalizedType}:${item?.id || ""}`,
    amount,
    signedAmount: normalizedType === CASH_FLOW_TYPE.OUTCOME ? -amount : amount,
    isApproved: isCashFlowApproved(item),
    isIncludedInStats: isCashFlowIncludedInStats(item),
    displayDate: item?.at || item?.date || item?.createdAt || "",
    period: getCashFlowPeriod(item),
    voucherCode: getCashFlowVoucherCode({ ...item, type: normalizedType }),
    category: getCashFlowCategory({ ...item, type: normalizedType }),
  };
};

export const getCashFlowDate = (item) => {
  const candidates = [item?.date, item?.createdAt, item?.at, item?.updatedAt];
  for (let index = 0; index < candidates.length; index += 1) {
    const parsedDate = parseCashFlowDateValue(candidates[index]);
    if (parsedDate) return parsedDate;
  }
  return null;
};

export const getCashFlowDateKey = (item) => {
  const date = getCashFlowDate(item);
  if (!date) return "";
  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;
};

export const matchesCashFlowPeriod = (item, periodFilter) => {
  if (!periodFilter) return true;
  const dateKey = getCashFlowDateKey(item);
  if (!dateKey) return false;
  return periodFilter.length === 10 ? dateKey === periodFilter : dateKey.slice(0, 7) === periodFilter;
};

export const getCashFlowPeriod = (item) => {
  const date = getCashFlowDate(item);
  if (!date) return item?.date || "";

  return `${padNumber(date.getMonth() + 1)}/${date.getFullYear()}`;
};

export const getCashFlowVoucherCode = (item) => {
  const prefix = item?.type === CASH_FLOW_TYPE.OUTCOME ? "PC" : "PT";
  const date = getCashFlowDate(item);
  const compactDate = date
    ? `${String(date.getFullYear()).slice(-2)}${padNumber(date.getMonth() + 1)}${padNumber(
        date.getDate(),
      )}`
    : "000000";
  const idSeed = String(item?.id || "")
    .replace(/[^a-z0-9]/gi, "")
    .slice(-3)
    .toUpperCase();

  return `${prefix}${compactDate}-${idSeed || "001"}`;
};

export const getCashFlowCategory = (item) => {
  const haystack = [item?.alias, item?.sub_title, item?.name, item?.note]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (item?.type === CASH_FLOW_TYPE.INCOME) {
    if (/cọc|deposit/.test(haystack)) return "Thu cọc";
    if (/hoàn|refund/.test(haystack)) return "Hoàn tiền";
    return "Thu khác";
  }

  if (/internet|wifi|mạng/.test(haystack)) return "Internet";
  if (/điện|dien|electric/.test(haystack)) return "Điện";
  if (/nước|nuoc|water/.test(haystack)) return "Nước";
  if (/lương|luong|salary|nhân viên/.test(haystack)) return "Lương";
  if (/mặt bằng|mat bang|thuê|rent|location/.test(haystack)) return "Mặt bằng";
  if (/marketing|ads|quảng cáo|facebook/.test(haystack)) return "Marketing";
  if (/vận chuyển|van chuyen|ship|delivery/.test(haystack)) return "Vận chuyển";

  return "Chi khác";
};

export const isRecurringCashFlowItem = (item) =>
  item?.type === CASH_FLOW_TYPE.OUTCOME &&
  RECURRING_CASH_FLOW_CATEGORIES.includes(item?.category || getCashFlowCategory(item));

export const buildRecurringCashFlowSummary = (items = []) =>
  RECURRING_CASH_FLOW_CATEGORIES.reduce((summary, category) => {
    const categoryItems = items.filter(
      (item) => item?.category === category && isRecurringCashFlowItem(item),
    );
    const approvedItems = categoryItems.filter(
      (item) => item.isApproved && item.isIncludedInStats,
    );
    const pendingItems = categoryItems.filter(
      (item) => item.status === CASH_FLOW_STATUS.PENDING,
    );

    summary[category] = {
      count: categoryItems.length,
      grossTotal: categoryItems.reduce(
        (total, item) => total + parseCashFlowAmount(item.amount ?? item.value),
        0,
      ),
      approvedTotal: approvedItems.reduce(
        (total, item) => total + parseCashFlowAmount(item.amount ?? item.value),
        0,
      ),
      pendingTotal: pendingItems.reduce(
        (total, item) => total + parseCashFlowAmount(item.amount ?? item.value),
        0,
      ),
      pendingCount: pendingItems.length,
    };

    return summary;
  }, {});

export const buildCashFlowSummary = (items = []) => {
  const summary = {
    totalIncome: 0,
    totalOutcome: 0,
    netCashFlow: 0,
    grossIncome: 0,
    grossOutcome: 0,
    pendingAmount: 0,
    pendingCount: 0,
    rejectedCount: 0,
    excludedCount: 0,
    incomeCount: 0,
    outcomeCount: 0,
    totalCount: items.length,
  };

  items.forEach((item) => {
    const amount = parseCashFlowAmount(item.amount ?? item.value);
    const isIncome = item.type === CASH_FLOW_TYPE.INCOME;
    const isOutcome = item.type === CASH_FLOW_TYPE.OUTCOME;

    if (isIncome) {
      summary.incomeCount += 1;
      summary.grossIncome += amount;
    }

    if (isOutcome) {
      summary.outcomeCount += 1;
      summary.grossOutcome += amount;
    }

    if (item.status === CASH_FLOW_STATUS.PENDING) {
      summary.pendingCount += 1;
      summary.pendingAmount += amount;
    }

    if (item.status === CASH_FLOW_STATUS.REJECTED) {
      summary.rejectedCount += 1;
    }

    if (!item.isIncludedInStats) {
      summary.excludedCount += 1;
    }

    if (!item.isApproved || !item.isIncludedInStats) return;

    if (isIncome) summary.totalIncome += amount;
    if (isOutcome) summary.totalOutcome += amount;
  });

  summary.netCashFlow = summary.totalIncome - summary.totalOutcome;

  return summary;
};

export const filterCashFlowItems = (
  items = [],
  { type = "all", status = "all", statistic = "all", search = "" } = {},
) => {
  const keyword = String(search || "").trim().toLowerCase();

  return items.filter((item) => {
    if (type !== "all" && item.type !== type) return false;
    if (status !== "all" && item.status !== status) return false;
    if (statistic === "included" && !item.isIncludedInStats) return false;
    if (statistic === "excluded" && item.isIncludedInStats) return false;

    if (!keyword) return true;

    return [item.sub_title, item.name, item.note, item.address, item.status]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(keyword));
  });
};

export const sortCashFlowItems = (items = []) =>
  [...items].sort((a, b) => {
    const firstDate = getCashFlowDate(a)?.getTime() || 0;
    const secondDate = getCashFlowDate(b)?.getTime() || 0;
    return secondDate - firstDate;
  });
